import { Plus, RotateCcw, UserPlus, Users, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useLockBodyScroll } from '../lib/use-lock-body-scroll';

export interface RosterGroup {
  key: string;
  label: string;
  icon: ReactNode;
  countClass: string;
  rowClass: string;
  /** Kräftigere Variante von `rowClass`, für Einträge mit `isTrainer: true`
   * (§ Nutzeranfrage: ein an-/abgemeldeter Trainer soll in der
   * Teilnehmerliste kräftiger hervorgehoben sein). Ohne diese Prop fallen
   * solche Einträge auf `rowClass` zurück wie jeder andere. */
  trainerRowClass?: string;
  items: Array<{ id: string; name: string; hint?: string; isTrainer?: boolean }>;
}

/** Debounced name search + pick, shown when the "+" button (only rendered
 * when the caller passed all three add-player props) is toggled open —
 * mirrors LineupModal's "Spieler hinzufügen" search, just registering a
 * response instead of a lineup slot. */
function AddPlayerSearch({
  onSearch,
  onAdd,
  onDone,
}: {
  onSearch: (query: string) => Promise<Array<{ id: string; name: string }>>;
  onAdd: (id: string) => Promise<void>;
  onDone: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<{ id: string; name: string }>>([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const handle = setTimeout(() => {
      onSearch(q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [query, onSearch]);

  return (
    <div className="mb-4 rounded-lg border border-slate-200 p-2">
      <input
        autoFocus
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Name eingeben…"
        className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
      />
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {query.trim().length >= 2 && (
        <ul className="mt-2 max-h-40 space-y-0.5 overflow-y-auto">
          {searching && <li className="px-2 py-1 text-xs text-slate-400">Suche…</li>}
          {!searching && results.length === 0 && <li className="px-2 py-1 text-xs text-slate-400">Keine Treffer.</li>}
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                disabled={addingId === r.id}
                onClick={async () => {
                  setAddingId(r.id);
                  setError(null);
                  try {
                    await onAdd(r.id);
                    onDone();
                  } catch {
                    setError('Anmelden fehlgeschlagen.');
                  } finally {
                    setAddingId(null);
                  }
                }}
                className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                <span>{r.name}</span>
                {addingId === r.id ? (
                  <span className="text-xs text-slate-400">wird angemeldet…</span>
                ) : (
                  <UserPlus size={14} className="text-slate-400" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Shared roster popup for season matches and training events — grouped
 * name lists (e.g. Zugesagt/Abgesagt) with an optional per-row reset
 * (undo) action for whoever can manage the underlying vote/registration.
 * The "+" search (only for season matches so far, `onSearchPlayers`/
 * `onAddPlayer` both required together with `canAddPlayer`) lets a team
 * manager register someone who hasn't responded themselves. */
export function RosterModal({
  title,
  subtitle,
  groups,
  onClose,
  onReset,
  resettingId,
  canAddPlayer,
  onSearchPlayers,
  onAddPlayer,
  headerExtra,
}: {
  title: string;
  subtitle: string;
  groups: RosterGroup[];
  onClose: () => void;
  onReset?: (id: string) => void;
  resettingId?: string | null;
  canAddPlayer?: boolean;
  onSearchPlayers?: (query: string) => Promise<Array<{ id: string; name: string }>>;
  onAddPlayer?: (id: string) => Promise<void>;
  /** Extra content between the subtitle and the group lists — e.g. the
   * Calendar's click-to-respond popup's own Zusagen/Absagen/Vielleicht
   * buttons (`CalendarPage.tsx`'s `CalendarMatchPopup`), which have no
   * other home since this modal has no "respond for yourself" concept of
   * its own (unlike `TrainingMenuModal`). */
  headerExtra?: ReactNode;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const showAddButton = Boolean(canAddPlayer && onSearchPlayers && onAddPlayer);
  useLockBodyScroll();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <Users size={18} />
            {title}
          </h2>
          <div className="flex items-center gap-1">
            {showAddButton && (
              <button
                type="button"
                title="Spieler manuell anmelden"
                aria-label="Spieler manuell anmelden"
                onClick={() => setSearchOpen((o) => !o)}
                className={`rounded-md p-1 ${
                  searchOpen ? 'bg-brand-accent/10 text-brand-accent' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                }`}
              >
                <Plus size={18} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <p className="mb-4 text-xs text-slate-500">{subtitle}</p>

        {headerExtra}

        {searchOpen && canAddPlayer && onSearchPlayers && onAddPlayer && (
          <AddPlayerSearch onSearch={onSearchPlayers} onAdd={onAddPlayer} onDone={() => setSearchOpen(false)} />
        )}

        {groups.map((g) => (
          <div key={g.key} className="mb-4 last:mb-0">
            <div className="mb-1.5 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
              <span>{g.label}</span>
              <span className={g.countClass}>{g.items.length}</span>
            </div>
            {g.items.length === 0 ? (
              <p className="py-1 text-xs text-slate-400">—</p>
            ) : (
              <ul className="space-y-1">
                {g.items.map((item) => (
                  <li
                    key={item.id}
                    className={`flex items-center justify-between rounded-md px-2 py-1.5 text-sm ${
                      item.isTrainer && g.trainerRowClass ? g.trainerRowClass : g.rowClass
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {g.icon}
                      {item.name}
                      {item.hint && <span className="text-xs font-normal text-slate-400">({item.hint})</span>}
                    </span>
                    {onReset && (
                      <button
                        type="button"
                        title="Zurücksetzen"
                        onClick={() => onReset(item.id)}
                        disabled={resettingId === item.id}
                        className="rounded p-1 text-slate-400 hover:bg-white/70 hover:text-slate-600 disabled:opacity-40"
                      >
                        <RotateCcw size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
